import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, Target, Waves } from "lucide-react";

function reliableBadge(reliable) {
  if (reliable) return <span className="text-[10px] font-mono-num text-invest">fiable</span>;
  return <span className="text-[10px] font-mono-num text-ignore uppercase tracking-widest">échantillon insuffisant</span>;
}

function fmtSigned(n) {
  if (n == null || isNaN(n)) return "—";
  return `${n >= 0 ? "+" : ""}${Number(n).toFixed(2)} $`;
}

function StrategyShowdown({ data }) {
  if (!data || data.days.length === 0) {
    return (
      <div className="text-xs text-muted-2 p-6">Pas encore assez de journées complètes pour comparer.</div>
    );
  }
  const total = data.sniper_wins + data.filet_wins + data.ties;
  const sniperPct = total > 0 ? (data.sniper_wins / total * 100).toFixed(0) : 0;
  const filetPct = total > 0 ? (data.filet_wins / total * 100).toFixed(0) : 0;
  const delta = data.cumulative_delta_pnl;
  const leader = delta > 0 ? "Sniper" : delta < 0 ? "Filet" : "Égalité";
  return (
    <div className="p-6 space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg p-3 text-center" style={{ background: "rgba(0,230,118,0.08)", border: "1px solid rgba(0,230,118,0.3)" }}>
          <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest text-invest mb-1"><Target size={11} /> Sniper</div>
          <div className="font-mono-num text-2xl text-invest font-semibold" data-testid="showdown-sniper-wins">{data.sniper_wins}</div>
          <div className="text-[10px] text-muted-2">{sniperPct}% victoires</div>
          <div className="font-mono-num text-xs text-muted-1 mt-1">Total P&L : {fmtSigned(data.sniper_total)}</div>
        </div>
        <div className="rounded-lg p-3 text-center bg-card-2 border border-line">
          <div className="text-[10px] uppercase tracking-widest text-muted-2 mb-1">Égalités</div>
          <div className="font-mono-num text-2xl text-app">{data.ties}</div>
          <div className="text-[10px] text-muted-2">sur {total} jours</div>
        </div>
        <div className="rounded-lg p-3 text-center" style={{ background: "rgba(0,176,255,0.08)", border: "1px solid rgba(0,176,255,0.3)" }}>
          <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest" style={{ color: "#00B0FF" }}><Waves size={11} /> Filet</div>
          <div className="font-mono-num text-2xl font-semibold" style={{ color: "#00B0FF" }} data-testid="showdown-filet-wins">{data.filet_wins}</div>
          <div className="text-[10px] text-muted-2">{filetPct}% victoires</div>
          <div className="font-mono-num text-xs text-muted-1 mt-1">Total P&L : {fmtSigned(data.filet_total)}</div>
        </div>
      </div>
      <div className="text-center py-3 border-t border-line" data-testid="showdown-delta">
        <div className="text-[10px] uppercase tracking-widest text-muted-2 mb-1">Écart cumulé (Sniper − Filet)</div>
        <div className={`font-mono-num text-3xl font-semibold ${delta >= 0 ? "text-invest" : "text-ignore"}`}>
          {delta >= 0 ? "+" : ""}{Number(delta).toFixed(2)} $
        </div>
        <div className="text-xs text-muted-1 mt-1">
          Sur {total} jour(s), <span className="font-medium">{leader}</span> mène.
          Cette comparaison est purement rétroactive — n'affecte ni le capital ni la réserve.
        </div>
      </div>
    </div>
  );
}

export default function LearnedModel() {
  const [m, setM] = useState(null);
  const [showdown, setShowdown] = useState(null);
  const [strategyFilter, setStrategyFilter] = useState("all"); // all | sniper | filet

  useEffect(() => {
    const q = strategyFilter === "all" ? "" : `?strategy=${strategyFilter}`;
    api.get(`/learned-model${q}`).then((r) => setM(r.data));
  }, [strategyFilter]);
  useEffect(() => { api.get("/strategy-showdown").then((r) => setShowdown(r.data)); }, []);
  if (!m) return <div className="text-muted-1">Chargement…</div>;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-2 mb-1">Vérification</div>
        <h1 className="font-display text-3xl font-semibold">Modèle appris</h1>
        <p className="text-sm text-muted-1 mt-2 max-w-2xl">
          Ces statistiques sont construites à partir de l'historique réel de vos clôtures.
          Elles ne prédisent rien : elles mesurent si les règles de scoring tiennent la route sur vos propres données.
        </p>
      </div>

      {/* Iter 27 — Sniper vs Filet showdown (across all days) */}
      <div className="bg-card-1 border border-line rounded-xl overflow-hidden">
        <div className="p-6 border-b border-line flex items-center justify-between">
          <h2 className="font-display text-lg">Sniper vs Filet — bilan cumulé</h2>
        </div>
        <StrategyShowdown data={showdown} />
      </div>

      {/* Strategy filter for the rest of the model */}
      <div className="flex gap-2" data-testid="learned-strategy-filter">
        {[
          { code: "all", label: "Toutes" },
          { code: "sniper", label: "Sniper" },
          { code: "filet", label: "Filet" },
        ].map((opt) => (
          <button
            key={opt.code}
            onClick={() => setStrategyFilter(opt.code)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              strategyFilter === opt.code
                ? "bg-invest/15 border-invest text-invest"
                : "border-line text-muted-1 hover:text-app"
            }`}
            data-testid={`learned-strategy-${opt.code}`}
          >{opt.label}</button>
        ))}
      </div>

      {m.total_observations < 20 && (
        <div className="bg-ignore/10 border border-ignore/25 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-ignore mt-0.5" />
          <div className="text-xs font-mono-num text-ignore">
            Attention : seulement {m.total_observations} observation(s) avec clôture renseignée.
            Il faut au moins 50 à 100 observations avant de tirer des conclusions.
          </div>
        </div>
      )}

      {/* Buckets */}
      <div className="bg-card-1 border border-line rounded-xl overflow-hidden">
        <div className="p-6 border-b border-line">
          <h2 className="font-display text-lg">Par tranche de score</h2>
        </div>
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto] gap-4 px-6 py-3 border-b border-line text-[10px] uppercase tracking-widest text-muted-2">
          <div>Tranche</div><div className="text-right">N</div><div className="text-right">Succès</div>
          <div className="text-right">Gain moy.</div><div className="text-right">Perte moy.</div>
          <div className="text-right">Espérance</div><div className="text-right">Fiabilité</div>
        </div>
        {m.buckets.map((b) => (
          <div key={b.bucket} className="grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto] gap-4 px-6 py-3 border-b border-line last:border-b-0 items-center">
            <div className="font-mono-num">{b.bucket}</div>
            <div className="text-right font-mono-num">{b.n}</div>
            <div className="text-right font-mono-num">{b.success_rate_pct}%</div>
            <div className="text-right font-mono-num text-invest">+{b.avg_win_pct}%</div>
            <div className="text-right font-mono-num text-ignore">{b.avg_loss_pct}%</div>
            <div className={`text-right font-mono-num ${b.expectancy_pct >= 0 ? "text-invest" : "text-ignore"}`}>{b.expectancy_pct >= 0 ? "+" : ""}{b.expectancy_pct}%</div>
            <div className="text-right">{reliableBadge(b.reliable)}</div>
          </div>
        ))}
      </div>

      {/* Criteria edge */}
      <div className="bg-card-1 border border-line rounded-xl overflow-hidden">
        <div className="p-6 border-b border-line">
          <h2 className="font-display text-lg">Edge par critère</h2>
          <p className="text-xs text-muted-1 mt-1">Rendement moyen des actions où ce critère est fort vs. faible.</p>
        </div>
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-4 px-6 py-3 border-b border-line text-[10px] uppercase tracking-widest text-muted-2">
          <div>Critère</div><div className="text-right">N fort</div><div className="text-right">Moy fort</div>
          <div className="text-right">N faible</div><div className="text-right">Moy faible</div><div className="text-right">Edge</div>
        </div>
        {m.criteria.map((c) => (
          <div key={c.criterion} className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-4 px-6 py-3 border-b border-line last:border-b-0 items-center">
            <div className="text-sm">{c.label}</div>
            <div className="text-right font-mono-num">{c.n_high}</div>
            <div className="text-right font-mono-num">{c.avg_high_pct != null ? `${c.avg_high_pct}%` : "—"}</div>
            <div className="text-right font-mono-num">{c.n_low}</div>
            <div className="text-right font-mono-num">{c.avg_low_pct != null ? `${c.avg_low_pct}%` : "—"}</div>
            <div className={`text-right font-mono-num ${c.edge_pct == null ? "text-muted-2" : c.edge_pct >= 0 ? "text-invest" : "text-ignore"}`}>
              {c.edge_pct == null ? "—" : `${c.edge_pct >= 0 ? "+" : ""}${c.edge_pct}%`}
              {!c.reliable && <span className="ml-2 text-[9px] text-ignore uppercase">n/a</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Rank */}
      <div className="bg-card-1 border border-line rounded-xl overflow-hidden">
        <div className="p-6 border-b border-line">
          <h2 className="font-display text-lg">Rang de départ vs. rendement</h2>
          <p className="text-xs text-muted-1 mt-1">Est-ce que la position dans le screener prédit déjà une partie du résultat ?</p>
        </div>
        {m.ranks.length === 0 ? (
          <div className="p-6 text-sm text-muted-1">Aucune observation avec un rang renseigné.</div>
        ) : (
          <>
            <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 px-6 py-3 border-b border-line text-[10px] uppercase tracking-widest text-muted-2">
              <div>Rang</div><div /><div className="text-right">N</div>
              <div className="text-right">% moyen</div><div className="text-right">Succès</div>
            </div>
            {m.ranks.map((r) => (
              <div key={r.rank} className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 px-6 py-3 border-b border-line last:border-b-0 items-center">
                <div className="font-mono-num">#{r.rank}</div>
                <div>{!r.reliable && <span className="text-[9px] text-ignore uppercase tracking-widest">échantillon insuffisant</span>}</div>
                <div className="text-right font-mono-num">{r.n}</div>
                <div className={`text-right font-mono-num ${r.avg_pct >= 0 ? "text-invest" : "text-ignore"}`}>{r.avg_pct >= 0 ? "+" : ""}{r.avg_pct}%</div>
                <div className="text-right font-mono-num">{r.success_rate_pct}%</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
